import { startOfWeek, endOfWeek, isBefore, startOfDay } from 'date-fns';
import { isPrivilege, UserPrivileges } from '../rules';

/**
 * Scheduling configuration interface
 */
export interface SchedulingConfig {
    week_start_day?: number; // 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.
    allow_past_days_shift?: boolean; // Allow editing shifts for past days
    RESTRICT_TO_PREDEFINED_SHIFTS?: boolean; // Require users to choose from shift templates only
}

/**
 * Default scheduling configuration
 */
export const DEFAULT_SCHEDULING_CONFIG: Required<SchedulingConfig> = {
    week_start_day: 0, // Default to Sunday
    allow_past_days_shift: false, // Default to not allowing past days
    RESTRICT_TO_PREDEFINED_SHIFTS: false, // Default to preserving existing free-flow scheduling behavior
};

const getBooleanConfigValue = (value: unknown, defaultValue: boolean): boolean => {
    if (value === undefined || value === null || value === "") {
        return defaultValue;
    }
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value === 1;
    }
    if (typeof value === "string") {
        return ["1", "true", "yes", "on"].includes(value.trim().toLowerCase());
    }
    return defaultValue;
};

/**
 * Scheduling Tool Utility Class
 * Provides utility functions for scheduling tool configuration and date calculations
 */
export class SchedulingToolUtils {
    /**
     * Get scheduling configuration from user object
     * @param user - User object that may contain scheduling_config
     * @returns SchedulingConfig with defaults applied
     */
    static getSchedulingConfig(user: any): Required<SchedulingConfig> {
        const config = user?.scheduling_config || {};
        
        return {
            week_start_day: config.week_start_day ?? DEFAULT_SCHEDULING_CONFIG.week_start_day,
            allow_past_days_shift: config.allow_past_days_shift ?? DEFAULT_SCHEDULING_CONFIG.allow_past_days_shift,
            RESTRICT_TO_PREDEFINED_SHIFTS: getBooleanConfigValue(
                config.RESTRICT_TO_PREDEFINED_SHIFTS,
                DEFAULT_SCHEDULING_CONFIG.RESTRICT_TO_PREDEFINED_SHIFTS
            ),
        };
    }

    /**
     * Check if user is superadmin (account owner or super user).
     * Uses is_account_owner and is_super_user from actor model (API may send is_super_user as number, "1", or boolean).
     * @param user - User object with is_account_owner and is_super_user
     * @returns True if user is account owner or super user
     */
    static isSuperAdmin(user: any): boolean {
        if (!user) return false;
        if (user.is_account_owner === true) return true;
        const su = user.is_super_user;
        return su === 1 || su === true || su === "1" || Number(su) === 1;
    }

    /**
     * Check if user has schedule tool privilege (admin or schedule tool privilege or is account owner or super user)
     * @param user - User object with employee_access_role_privileges, is_account_owner, and is_super_user
     * @returns True if user has admin or schedule tool privilege or is account owner or super user, false otherwise (view-only mode)
     */
    static hasScheduleToolPrivilege(user: any): boolean {
        const userPrivileges = user?.employee_access_role_privileges;
        const isAdmin = isPrivilege({
            privileges: userPrivileges,
            rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
        });
        const hasSchedulePrivilege = isPrivilege({
            privileges: userPrivileges,
            rule: UserPrivileges.EMPLOYEE_PRIV_SCHEDULE_TOOL,
        });
        return hasSchedulePrivilege || isAdmin || this.isSuperAdmin(user);
    }

    /**
     * Check if user is in view-only mode (does not have schedule tool privilege)
     * @param user - User object with employee_access_role_privileges
     * @returns True if user is in view-only mode, false otherwise
     */
    static isViewOnlyMode(user: any): boolean {
        return !this.hasScheduleToolPrivilege(user);
    }

    /**
     * Get week start day as date-fns format (0-6)
     * @param config - Scheduling configuration
     * @returns Week start day (0 = Sunday, 1 = Monday, etc.)
     */
    static getWeekStartDay(config: Required<SchedulingConfig>): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
        const day = config.week_start_day;
        // Ensure it's a valid day (0-6)
        if (day >= 0 && day <= 6) {
            return day as 0 | 1 | 2 | 3 | 4 | 5 | 6;
        }
        return 0; // Default to Sunday
    }

    /**
     * Check if a date is in the past (before today)
     * @param date - Date to check
     * @returns True if date is before today
     */
    static isPastDate(date: Date): boolean {
        const today = startOfDay(new Date());
        const checkDate = startOfDay(date);
        return isBefore(checkDate, today);
    }

    /**
     * Check if editing is allowed for a given date
     * @param date - Date to check
     * @param config - Scheduling configuration
     * @returns True if editing is allowed for this date
     */
    static canEditDate(date: Date, config: Required<SchedulingConfig>): boolean {
        // If past days are allowed, always return true
        if (config.allow_past_days_shift) {
            return true;
        }
        
        // Otherwise, only allow editing if the date is today or in the future
        return !this.isPastDate(date);
    }

    /**
     * Get start of week for a given date based on configuration
     * @param date - Date to get week start for
     * @param config - Scheduling configuration
     * @returns Start of week date
     */
    static getWeekStart(date: Date, config: Required<SchedulingConfig>): Date {
        const weekStartDay = this.getWeekStartDay(config);
        return startOfWeek(date, { weekStartsOn: weekStartDay });
    }

    /**
     * Get end of week for a given date based on configuration
     * @param date - Date to get week end for
     * @param config - Scheduling configuration
     * @returns End of week date
     */
    static getWeekEnd(date: Date, config: Required<SchedulingConfig>): Date {
        const weekStartDay = this.getWeekStartDay(config);
        return endOfWeek(date, { weekStartsOn: weekStartDay });
    }

    /**
     * Get publish status info for a schedule
     * @param schedule - Schedule object with publish field
     * @returns Object with icon name, tooltip text, and color class, or null if publish status is invalid
     */
    static getPublishStatusInfo(schedule: { publish?: string | number | null }): { iconName: 'XCircle' | 'CheckCircle2' | 'Clock'; tooltip: string; iconColor: string; isUnpublished: boolean } | null {
        // Parse publish status (handle both string and number)
        const publishStatus = schedule.publish === null || schedule.publish === undefined 
            ? 0 
            : typeof schedule.publish === 'string' 
                ? parseInt(schedule.publish, 10) 
                : schedule.publish;

        // If invalid number, default to 0
        if (isNaN(publishStatus) || publishStatus < 0 || publishStatus > 2) {
            return null;
        }

        switch (publishStatus) {
            case 0:
                // Not published
                return {
                    iconName: 'XCircle',
                    tooltip: 'Not Published',
                    iconColor: 'text-white',
                    isUnpublished: true
                };
            case 1:
                // Published
                return {
                    iconName: 'CheckCircle2',
                    tooltip: 'Published',
                    iconColor: 'text-green-400',
                    isUnpublished: false
                };
            case 2:
                return {
                    iconName: 'CheckCircle2',
                    tooltip: 'Published',
                    iconColor: 'text-green-400',
                    isUnpublished: false
                };
                // // Ready to publish
                // return {
                //     iconName: 'Clock',
                //     tooltip: 'Ready to Publish',
                //     iconColor: 'text-yellow-500'
                // };
            default:
                return null;
        }
    }

    /**
     * Get status info for an open shift.
     * @param openShift - Open shift object with status field
     * @returns Icon/tooltip/color metadata for known statuses
     */
    static getOpenShiftStatusInfo(openShift: { status?: string | null }): { iconName: 'Pencil' | 'CheckCircle2'; tooltip: string; iconColor: string; isDraft: boolean } | null {
        const rawStatus = openShift?.status;
        const normalizedStatus = typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : "";
        const numericStatus = Number(rawStatus);

        if (
            normalizedStatus === "draft" ||
            normalizedStatus === "unpublished" ||
            normalizedStatus === "not published" ||
            normalizedStatus === "not_published" ||
            numericStatus === 0
        ) {
            return {
                iconName: "Pencil",
                tooltip: "Draft",
                iconColor: "text-amber-600",
                isDraft: true,
            };
        }

        if (normalizedStatus === "published" || numericStatus === 1 || numericStatus === 2) {
            return {
                iconName: "CheckCircle2",
                tooltip: "Published",
                iconColor: "text-green-600",
                isDraft: false,
            };
        }

        return null;
    }
}
